import React, { useState, useMemo } from "react";
import {
  Box,
  Divider,
  Grid,
  Paper,
  Stack,
  TextField,
  Typography,
  Button,
  InputAdornment,
} from "@mui/material";
import { MonitorHeart, History } from "@mui/icons-material";
import { Controller } from "react-hook-form";
import { HistoryModal } from "./HistoryModal";
import { useVetAttendance } from "@/fluxvet/contexts/VetAttendanceContext";
import {
  extractHeartRateHistory,
  extractRespiratoryRateHistory,
  extractCapillaryRefillTimeHistory,
  extractHydrationHistory,
  extractTemperatureHistory,
} from "@/fluxvet/utils/questionnaireResponseHelper";

// =============================================================================
// TIPOS - Interfaces
// =============================================================================

/**
 * Props do componente VitalSignsSection
 */
interface VitalSignsSectionProps {
  control: any;
}

// =============================================================================
// COMPONENTE PRINCIPAL
// =============================================================================

/**
 * Componente de Sinais Vitais
 * Exibe formulário estruturado para coleta de sinais vitais do paciente
 * Inclui botões para visualizar histórico de cada parâmetro
 * 
 * Campos incluídos:
 * - FC (Frequência Cardíaca) em bpm
 * - FR (Frequência Respiratória) em mpm
 * - TPC (Tempo de Preenchimento Capilar) em segundos
 * - Hidratação (descrição)
 * - Temperatura em °C
 * 
 * @param control - Control do react-hook-form
 */
export const VitalSignsSection: React.FC<VitalSignsSectionProps> = ({
  control,
}) => {
  // Busca o histórico de QuestionnaireResponse do contexto
  const { historyQuestionnaireResponse } = useVetAttendance();

  // Processa o histórico para extrair dados de cada campo
  // Usa useMemo para evitar reprocessamento desnecessário
  const heartRateHistory = useMemo(
    () => extractHeartRateHistory(historyQuestionnaireResponse),
    [historyQuestionnaireResponse]
  );

  const respiratoryRateHistory = useMemo(
    () => extractRespiratoryRateHistory(historyQuestionnaireResponse),
    [historyQuestionnaireResponse]
  );

  const capillaryRefillTimeHistory = useMemo(
    () => extractCapillaryRefillTimeHistory(historyQuestionnaireResponse),
    [historyQuestionnaireResponse]
  );

  const hydrationHistory = useMemo(
    () => extractHydrationHistory(historyQuestionnaireResponse),
    [historyQuestionnaireResponse]
  );

  const temperatureHistory = useMemo(
    () => extractTemperatureHistory(historyQuestionnaireResponse),
    [historyQuestionnaireResponse]
  );

  // Estado único para controlar o modal de histórico
  const [historyModal, setHistoryModal] = useState<{
    isOpen: boolean;
    title: string;
    history: any[];
  }>({
    isOpen: false,
    title: "",
    history: [],
  });

  /**
   * Função auxiliar para abrir o modal de histórico
   */
  const openHistoryModal = (title: string, history: any[]) => {
    setHistoryModal({ isOpen: true, title, history });
  };

  /**
   * Função auxiliar para fechar o modal de histórico
   */
  const closeHistoryModal = () => {
    setHistoryModal({ isOpen: false, title: "", history: [] });
  };

  return (
    <>
      <Box
        sx={{
          maxHeight: "calc(100vh - 400px)",
          overflowY: "auto",
          pr: 1,
        }}
      >
        <Grid container spacing={2}>
          {/* Linha 1: FC, FR, TPC */}
          {/* Frequência Cardíaca */}
          <Grid item xs={12} md={4}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                bgcolor: "#fafafa",
                border: "1px solid #e0e0e0",
                borderRadius: 2,
              }}
            >
              <Stack spacing={1.5}>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <MonitorHeart sx={{ fontSize: 18, color: "#00A884" }} />
                    <Typography variant="subtitle2" fontWeight={600}>
                      FC (Frequência Cardíaca)
                    </Typography>
                  </Stack>
                  <Button
                    size="small"
                    startIcon={<History />}
                    onClick={() =>
                      openHistoryModal(
                        "Histórico - Frequência Cardíaca",
                        heartRateHistory
                      )
                    }
                    sx={{
                      textTransform: "none",
                      fontSize: "0.75rem",
                      color: "#666",
                      "&:hover": {
                        color: "#00A884",
                        bgcolor: "rgba(0, 168, 132, 0.08)",
                      },
                    }}
                  >
                    Ver histórico
                  </Button>
                </Stack>
                <Divider />
                <Controller
                  name="heartRate"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      type="number"
                      label="Frequência Cardíaca"
                      placeholder="Ex: 120"
                      error={!!error}
                      helperText={error?.message}
                      fullWidth
                      variant="outlined"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">bpm</InputAdornment>
                        ),
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": { bgcolor: "#fff" },
                      }}
                    />
                  )}
                />
              </Stack>
            </Paper>
          </Grid>

          {/* Frequência Respiratória */}
          <Grid item xs={12} md={4}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                bgcolor: "#fafafa",
                border: "1px solid #e0e0e0",
                borderRadius: 2,
              }}
            >
              <Stack spacing={1.5}>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <MonitorHeart sx={{ fontSize: 18, color: "#00A884" }} />
                    <Typography variant="subtitle2" fontWeight={600}>
                      FR (Frequência Respiratória)
                    </Typography>
                  </Stack>
                  <Button
                    size="small"
                    startIcon={<History />}
                    onClick={() =>
                      openHistoryModal(
                        "Histórico - Frequência Respiratória",
                        respiratoryRateHistory
                      )
                    }
                    sx={{
                      textTransform: "none",
                      fontSize: "0.75rem",
                      color: "#666",
                      "&:hover": {
                        color: "#00A884",
                        bgcolor: "rgba(0, 168, 132, 0.08)",
                      },
                    }}
                  >
                    Ver histórico
                  </Button>
                </Stack>
                <Divider />
                <Controller
                  name="respiratoryRate"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      type="number"
                      label="Frequência Respiratória"
                      placeholder="Ex: 30"
                      error={!!error}
                      helperText={error?.message}
                      fullWidth
                      variant="outlined"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">mpm</InputAdornment>
                        ),
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": { bgcolor: "#fff" },
                      }}
                    />
                  )}
                />
              </Stack>
            </Paper>
          </Grid>

          {/* TPC (Tempo de Preenchimento Capilar) */}
          <Grid item xs={12} md={4}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                bgcolor: "#fafafa",
                border: "1px solid #e0e0e0",
                borderRadius: 2,
              }}
            >
              <Stack spacing={1.5}>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <MonitorHeart sx={{ fontSize: 18, color: "#00A884" }} />
                    <Typography variant="subtitle2" fontWeight={600}>
                      TPC (Tempo Preench. Capilar)
                    </Typography>
                  </Stack>
                  <Button
                    size="small"
                    startIcon={<History />}
                    onClick={() =>
                      openHistoryModal(
                        "Histórico - TPC",
                        capillaryRefillTimeHistory
                      )
                    }
                    sx={{
                      textTransform: "none",
                      fontSize: "0.75rem",
                      color: "#666",
                      "&:hover": {
                        color: "#00A884",
                        bgcolor: "rgba(0, 168, 132, 0.08)",
                      },
                    }}
                  >
                    Ver histórico
                  </Button>
                </Stack>
                <Divider />
                <Controller
                  name="capillaryRefillTime"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      type="number"
                      label="TPC"
                      placeholder="Ex: 2"
                      error={!!error}
                      helperText={error?.message}
                      fullWidth
                      variant="outlined"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">seg</InputAdornment>
                        ),
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": { bgcolor: "#fff" },
                      }}
                    />
                  )}
                />
              </Stack>
            </Paper>
          </Grid>

          {/* Linha 2: Hidratação, Temperatura */}
          {/* Hidratação */}
          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                bgcolor: "#fafafa",
                border: "1px solid #e0e0e0",
                borderRadius: 2,
              }}
            >
              <Stack spacing={1.5}>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <MonitorHeart sx={{ fontSize: 18, color: "#00A884" }} />
                    <Typography variant="subtitle2" fontWeight={600}>
                      Hidratação
                    </Typography>
                  </Stack>
                  <Button
                    size="small"
                    startIcon={<History />}
                    onClick={() =>
                      openHistoryModal(
                        "Histórico - Hidratação",
                        hydrationHistory
                      )
                    }
                    sx={{
                      textTransform: "none",
                      fontSize: "0.75rem",
                      color: "#666",
                      "&:hover": {
                        color: "#00A884",
                        bgcolor: "rgba(0, 168, 132, 0.08)",
                      },
                    }}
                  >
                    Ver histórico
                  </Button>
                </Stack>
                <Divider />
                <Controller
                  name="hydration"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      label="Estado de hidratação"
                      placeholder="Ex: Hidratado, turgor normal"
                      error={!!error}
                      helperText={error?.message}
                      fullWidth
                      variant="outlined"
                      sx={{
                        "& .MuiOutlinedInput-root": { bgcolor: "#fff" },
                      }}
                    />
                  )}
                />
              </Stack>
            </Paper>
          </Grid>

          {/* Temperatura */}
          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                bgcolor: "#fafafa",
                border: "1px solid #e0e0e0",
                borderRadius: 2,
              }}
            >
              <Stack spacing={1.5}>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <MonitorHeart sx={{ fontSize: 18, color: "#00A884" }} />
                    <Typography variant="subtitle2" fontWeight={600}>
                      Temperatura
                    </Typography>
                  </Stack>
                  <Button
                    size="small"
                    startIcon={<History />}
                    onClick={() =>
                      openHistoryModal(
                        "Histórico - Temperatura",
                        temperatureHistory
                      )
                    }
                    sx={{
                      textTransform: "none",
                      fontSize: "0.75rem",
                      color: "#666",
                      "&:hover": {
                        color: "#00A884",
                        bgcolor: "rgba(0, 168, 132, 0.08)",
                      },
                    }}
                  >
                    Ver histórico
                  </Button>
                </Stack>
                <Divider />
                <Controller
                  name="temperature"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      type="number"
                      label="Temperatura"
                      placeholder="Ex: 38.5"
                      error={!!error}
                      helperText={error?.message}
                      fullWidth
                      variant="outlined"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">°C</InputAdornment>
                        ),
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": { bgcolor: "#fff" },
                      }}
                    />
                  )}
                />
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      </Box>

      {/* Modal único de histórico */}
      <HistoryModal
        open={historyModal.isOpen}
        onClose={closeHistoryModal}
        title={historyModal.title}
        history={historyModal.history}
      />
    </>
  );
};

export default VitalSignsSection;

